import React from 'react';

import LazyLoadedImage from 'common/LazyLoadedImage';
import { cn } from 'common/style/utils';
import Button from 'common/ui/ButtonV2';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import VStack from 'common/ui/VStack';

import type { LucideProps } from 'lucide-react';

export type AdminOnboardingOption = {
  label: string;
  name: string;
  icon?: React.ComponentType<LucideProps>;
  img?: string;
  description?: React.ReactNode;
};

type Props = {
  optionType?: 'button' | 'card';
  onChange: (selected: string[]) => void;
  selected: string[];
  options: AdminOnboardingOption[];
};

const AdminOnboardingMultiSelect = ({
  optionType = 'button',
  onChange,
  options,
  selected,
}: Props) => {
  const onToggle = (option: string, isSelected: boolean) => {
    if (isSelected) {
      onChange([...selected, option]);
      return;
    }

    onChange(selected.filter((selectedOption) => selectedOption !== option));
  };

  const handleOptionClick = (option: string) => {
    onToggle(option, !selected.includes(option));
  };

  return (
    <HStack
      className={cn('flex-wrap gap-2', {
        'grid grid-cols-1 sm:grid-cols-2 gap-2': optionType === 'card' && options.length > 3,
        flex: optionType !== 'card' || options.length <= 3,
      })}>
      {options.map((option) => {
        const isSelected = selected.includes(option.name);
        const variant = isSelected ? 'toggleOn' : 'toggleOff';
        const Icon = option.icon;

        if (optionType === 'card') {
          return (
            <button
              key={option.name}
              className={cn('unstyled-button w-full text-foreground', {
                'sm:flex-1': options.length <= 3,
              })}
              onClick={() => handleOptionClick(option.name)}>
              <VStack
                className={cn(
                  'box-border',
                  'rounded',
                  ' gap-1 p-3 w-full',
                  'transition-colors duration-300',
                  {
                    'border border-indigo-90': isSelected,
                    'base-border': !isSelected,
                  },
                )}>
                {Icon && <Icon />}
                {option.img && <LazyLoadedImage src={option.img} width="18" />}
                <div className="text-base font-medium">{option.label}</div>
                <div className="text-base text-gray-100">{option.description}</div>
              </VStack>
            </button>
          );
        }

        return (
          <Button
            key={option.name}
            onClick={() => handleOptionClick(option.name)}
            size="large"
            startIcon={option.icon}
            variant={variant}>
            <Flex className="gap-2 text-lg items-center">
              {option.img && <LazyLoadedImage src={option.img} width="18" />}
              {option.label}
            </Flex>
          </Button>
        );
      })}
    </HStack>
  );
};

export default AdminOnboardingMultiSelect;
